import { Background, Container, TextArea } from '@/components/wm';
import { apiRequest } from '@/utils/request';
import { Bell, Check, Coins, Copy, CornerDownLeft, FileDown, Lightbulb, MessageSquare, RefreshCw, Target, Zap } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const POLL_INTERVAL = 2000;
const MAX_POLLS = 300; // give up after ~10 minutes, when the api lets the chat be asked again
const MAX_QUESTION_LENGTH = 2000; // the api refuses longer questions too

// the chat answers in text, with links to the platform only: images and other links are dropped
const platformUrl = url => {
    try {
        const { hostname, protocol } = new URL(url, window.location.origin);
        return protocol === 'https:' && (hostname === 'wisemetering.com' || hostname.endsWith('.wisemetering.com')) ? url : '';
    } catch {
        return '';
    }
};

// each example needs its tool: the api lists the tools the plan and the role of the user open
const EXAMPLES = [
    { icon: Zap, key: 'example_consumption', tool: 'fetch_consumption' },
    { icon: Coins, key: 'example_costs', tool: 'fetch_costs' },
    { icon: Bell, key: 'example_alarms', tool: 'list_events' },
    { icon: Target, key: 'example_objectives', tool: 'fetch_objectives' },
    { icon: Lightbulb, key: 'example_opportunities', tool: 'list_opportunities' }
];

function Message ({ message }) {
    return (
        <div>
            {message.split('\n').map((line, index) => (
                // Use <Fragment> or <span> to provide a unique key for each item
                <React.Fragment key={index}>
                    {line}
                    {index !== message.split('\n').length - 1 && <br/>}
                </React.Fragment>
            ))}
        </div>
    );
}

// assistant answers render markdown (the model is instructed to use tables); user text stays literal
function MarkdownMessage ({ message }) {
    return (
        <ReactMarkdown
            components={{
                a: props => <a className="tw-underline" rel="noreferrer" target="_blank" {...props}/>,
                code: props => <code className="tw-bg-gray-200 tw-rounded tw-px-1 tw-text-[12px]" {...props}/>,
                ol: props => <ol className="tw-list-decimal tw-pl-5 tw-my-1 first:tw-mt-0 last:tw-mb-0" {...props}/>,
                p: props => <p className="tw-my-1 first:tw-mt-0 last:tw-mb-0" {...props}/>,
                // pre defaults to white-space: pre, which would push long code lines out of the bubble
                pre: props => <pre className="tw-whitespace-pre-wrap tw-break-words tw-bg-gray-200 tw-rounded tw-p-2 tw-my-1 first:tw-mt-0 last:tw-mb-0 tw-text-[12px]" {...props}/>,
                // a wide table scrolls inside the bubble; the classes undo the global table rules of ui_app.css (full width, uppercase small header, clickable rows)
                table: props => (
                    <div className="tw-overflow-x-auto tw-my-2 first:tw-mt-0 last:tw-mb-0">
                        <table className="tw-border-collapse !tw-w-auto !tw-mt-0" {...props}/>
                    </div>
                ),
                td: props => <td className="tw-border tw-border-solid tw-border-gray-300 tw-px-2 tw-py-1 tw-whitespace-nowrap" {...props}/>,
                th: props => <th className="tw-border tw-border-solid tw-border-gray-300 tw-px-2 tw-py-1 tw-text-left tw-font-medium tw-bg-gray-100 tw-whitespace-nowrap" {...props}/>,
                thead: props => <thead className="!tw-normal-case !tw-text-[1em] !tw-leading-normal" {...props}/>,
                tr: props => <tr className="!tw-cursor-auto !tw-text-[1em] !tw-leading-normal" {...props}/>,
                // tw-clear-float-list undoes the global `ul li { float: left }` from ui_app.css,
                // which otherwise collapses the bubble height and squeezes the meta line beside the floats
                ul: props => <ul className="tw-clear-float-list tw-list-disc tw-pl-5 tw-my-1 first:tw-mt-0 last:tw-mb-0" {...props}/>
            }}
            disallowedElements={['img']}
            remarkPlugins={[remarkGfm]}
            urlTransform={platformUrl}
        >
            {message}
        </ReactMarkdown>
    );
}

export default function Chat () {
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null); // { message, retryable, at, elapsed }
    const [initializing, setInitializing] = useState(true); // the last chat is still loading: asking now would start a new one over it
    const [chatId, setChatId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [credits, setCredits] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [copiedId, setCopiedId] = useState(null);

    // bumping the token cancels any in-flight polling loop (new chat, unmount)
    const pollToken = useRef(0);
    const messagesRef = useRef(null);
    const containerRef = useRef(null);
    const askStartRef = useRef(null); // unix seconds of the question being answered

    const organizationId = WiseMetering.currentOrganization?.id || null;

    const sleep = ms => new Promise(r => setTimeout(r, ms));
    const nowSeconds = () => Math.floor(Date.now() / 1000);
    const elapsedNow = () => (askStartRef.current ? Math.max(0, nowSeconds() - askStartRef.current) : 0);

    const failWith = (message, retryable) => setError({ message, retryable, at: nowSeconds(), elapsed: elapsedNow() });

    const applyChat = chat => {
        setChatId(chat.id);
        if (chat.messages) {
            setMessages(chat.messages);
        }
        if (chat.state === 'failed') {
            failWith(chat.error || i18next.t('chat.error_generic'), true);
        }
    };

    const loadCredits = async () => {
        try {
            const query = organizationId ? `?organization_id=${organizationId}` : '';
            const { data } = await apiRequest(`/chats/credits${query}`);
            setCredits(data);
        } catch (e) {
            console.error(e);
        }
    };

    const pollUntilIdle = async id => {
        const token = ++pollToken.current;

        for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
            await sleep(POLL_INTERVAL);
            if (pollToken.current !== token) {
                return;
            }

            try {
                const { data: chat } = await apiRequest(`/chats/${id}`);
                if (pollToken.current !== token) {
                    return;
                }

                if (chat.state !== 'processing') {
                    applyChat(chat);
                    setLoading(false);
                    loadCredits(); // the reply was charged, refresh the meters
                    return;
                }
            } catch (e) {
                console.error(e); // transient failure, keep polling
            }
        }

        failWith(i18next.t('chat.error_generic'), true);
        setLoading(false);
    };

    const loadLastChat = async () => {
        try {
            const query = organizationId ? `?organization_id=${organizationId}` : '';
            const { data: chats } = await apiRequest(`/chats${query}`);
            if (!chats.length) {
                return;
            }

            const { data: chat } = await apiRequest(`/chats/${chats[0].id}`);
            applyChat(chat);

            if (chat.state === 'processing') {
                askStartRef.current = chat.messages?.filter(m => m.role === 'user').at(-1)?.created_at || nowSeconds();
                setLoading(true);
                pollUntilIdle(chat.id);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setInitializing(false);
        }
    };

    useEffect(() => {
        loadCredits();
        loadLastChat();
        return () => {
            pollToken.current += 1;
        };
    }, []);

    const startNewChat = () => {
        pollToken.current += 1;
        setChatId(null);
        setMessages([]);
        setError(null);
        setInput('');
        setLoading(false);
    };

    const sendMessage = () => {
        const question = input.trim();
        if (question.length <= MAX_QUESTION_LENGTH) {
            sendQuestion(question, true);
        }
    };

    // only the input's own question clears it: Retry and the examples keep a draft being typed
    const sendQuestion = async (question, fromInput = false) => {
        if (!question || loading || initializing) {
            return;
        }

        const bubbleId = crypto.randomUUID();
        setError(null);
        askStartRef.current = nowSeconds();
        setMessages(prev => [...prev, { id: bubbleId, role: 'user', content: question, created_at: askStartRef.current }]);
        if (fromInput) {
            setInput('');
        }
        setLoading(true);

        try {
            let id = chatId;
            if (!id) {
                const { data: chat } = await apiRequest('/chats', 'POST', { organization_id: organizationId });
                id = chat.id;
                setChatId(id);
            }

            const { data: chat } = await apiRequest(`/chats/${id}/ask`, 'POST', { message: question });
            applyChat(chat);
            await pollUntilIdle(id);
        } catch (e) {
            console.error(e);
            // an answered request kept no question: it goes back to the input, and a chat replaced on another tab or device leaves room for a new one
            if (e.response) {
                setMessages(prev => prev.filter(m => m.id !== bubbleId));
                setInput(current => current || question);
                if (e.response.status === 404) {
                    setChatId(null);
                }
            }
            failWith(e.response?.data?.error || i18next.t('chat.error_generic'), !e.response);
            setLoading(false);
        }
    };

    const retryLastQuestion = () => {
        const question = messages.filter(m => m.role === 'user').at(-1);
        if (question) {
            sendQuestion(question.content);
        }
    };

    const copyMessage = async message => {
        try {
            await navigator.clipboard.writeText(message.content);
            setCopiedId(message.id);
            setTimeout(() => setCopiedId(null), 1500);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (loading) {
            return;
        }

        const inputEl = document.getElementById('chat-input');
        inputEl.focus();
    }, [loading]);

    // shrink the textarea back when the message is sent or cleared
    useEffect(() => {
        if (input !== '') {
            return;
        }

        const inputEl = document.getElementById('chat-input');
        if (inputEl) {
            inputEl.style.height = '';
        }
    }, [input]);

    // live seconds counter while a reply is being generated
    useEffect(() => {
        if (!loading) {
            return undefined;
        }

        const tick = () => setElapsed(elapsedNow());
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [loading]);

    const formatTime = unix => {
        if (!unix) {
            return null;
        }

        const date = new Date(unix * 1000);
        const now = new Date();
        if (date.toDateString() === now.toDateString()) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        }

        // seconds matter while the conversation is happening; on older days the date and minute are enough
        const day = { day: '2-digit', month: '2-digit', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) };
        return `${date.toLocaleDateString([], day)} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    };

    const formatBytes = bytes => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`);

    const formatDuration = seconds => {
        // == null caught both null and undefined; keep that exact behaviour
        // now that eqeqeq bans it.
        if (seconds === null || seconds === undefined) {
            return null;
        }
        if (seconds < 60) {
            return `${seconds}s`;
        }

        const rest = seconds % 60;
        return `${Math.floor(seconds / 60)}m${rest ? `${rest}s` : ''}`;
    };

    // question meta: time; answer meta: time · duration · credits
    const metaFor = message => {
        const parts = [formatTime(message.created_at)];

        if (message.role !== 'user') {
            if (message.duration_seconds !== null && message.duration_seconds !== undefined) {
                parts.push(formatDuration(message.duration_seconds));
            }
            if (message.credits !== null && message.credits !== undefined) {
                parts.push(`${message.credits.toLocaleString()} ${i18next.t('chat.credits_label', { count: message.credits })}`);
            }
        }

        return parts.filter(Boolean).join(' · ');
    };

    // scroll lives inside the messages pane only; repeated shots cover late layout (fonts, height fitting)
    useEffect(() => {
        const pane = messagesRef.current;
        if (!pane) {
            return undefined;
        }

        const toBottom = () => {
            pane.scrollTop = pane.scrollHeight;
        };
        toBottom();
        requestAnimationFrame(toBottom);
        const timer = setTimeout(toBottom, 150);
        return () => clearTimeout(timer);
    }, [messages, loading, error]);

    // size the card to fit exactly inside the app scroll panel (#content), so the page itself never scrolls
    useEffect(() => {
        const panel = document.getElementById('content');
        if (panel) {
            panel.scrollTop = 0;
        }

        const fitHeight = () => {
            const el = containerRef.current;
            if (!el || !panel) {
                return;
            }

            const offsetTop = el.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop;
            el.style.height = `${panel.clientHeight - offsetTop - 16}px`;
        };

        fitHeight();
        window.addEventListener('resize', fitHeight);
        return () => window.removeEventListener('resize', fitHeight);
    }, []);

    return (
        <Background className="!tw-py-4 !tw-min-h-0">
            <Container className="tw-flex-col !tw-p-0 !tw-flex-none" ref={containerRef} variant="full">
                {/* Toolbar — always visible */}
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-4 tw-py-2 tw-border-b tw-border-solid tw-border-gray-200 tw-shrink-0">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button
                            className="tw-flex tw-items-center tw-gap-1.5 tw-h-8 tw-text-xs tw-text-white tw-bg-secondary tw-border tw-border-solid tw-border-secondary tw-rounded tw-px-3 hover:tw-opacity-90 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                            disabled={loading || (!messages.length && !chatId)}
                            onClick={startNewChat}
                            type="button"
                        >
                            <MessageSquare className="tw-h-3.5 tw-w-3.5"/>
                            {i18next.t('chat.new_chat')}
                        </button>
                        <span
                            className="tw-cursor-help tw-rounded tw-border tw-border-solid tw-border-secondary tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-secondary"
                            title={i18next.t('chat.beta_tooltip')}
                        >
                            {i18next.t('chat.beta')}
                        </span>
                    </div>
                    {credits && (
                        <div className="tw-flex tw-items-center tw-gap-4">
                            <div className="tw-text-right tw-text-xs tw-leading-5">
                                <div
                                    className="tw-text-gray-500"
                                    title={`${credits.user.remaining.toLocaleString()}/${credits.user.limit.toLocaleString()}`}
                                >
                                    {i18next.t('chat.user_credits')} <span className="tw-text-gray-900 tw-font-medium">{credits.user.percent_remaining}%</span>
                                </div>
                                <div className="tw-text-[11px] tw-text-gray-400">
                                    {i18next.t('chat.renews_on', { month: new Date(credits.user.resets_at * 1000).toLocaleDateString(i18next.language, { month: 'long' }), interpolation: { escapeValue: false } })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Messages — the only scrollable area */}
                <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-flex tw-flex-col tw-pl-4 tw-pr-6 tw-py-3" ref={messagesRef}>
                    {messages.length === 0 && !loading && !initializing ? (
                        <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-px-6">
                            <div className="tw-text-center">
                                <p className="tw-text-[15px] tw-font-medium tw-text-gray-900 tw-mb-0.5">{i18next.t('chat.welcome_title')}</p>
                                <p className="tw-text-[13px] tw-text-gray-500">{i18next.t('chat.welcome_subtitle')}</p>
                            </div>
                            <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-w-full tw-max-w-[560px]">
                                {EXAMPLES.filter(({ tool }) => credits?.tools?.includes(tool)).slice(0, 4).map(({ icon: Icon, key }) => (
                                    <button
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-border tw-border-solid tw-border-gray-200 tw-rounded tw-px-3 tw-py-2 tw-text-xs tw-text-gray-500 tw-text-left tw-bg-white hover:tw-bg-grey-bg hover:tw-text-gray-900"
                                        key={key}
                                        onClick={() => sendQuestion(i18next.t(`chat.${key}`))}
                                        type="button"
                                    >
                                        <Icon className="tw-h-3.5 tw-w-3.5 tw-shrink-0"/>
                                        {i18next.t(`chat.${key}`)}
                                    </button>
                                ))}
                            </div>
                            <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-max-w-[520px] tw-leading-relaxed">
                                {i18next.t('chat.welcome_scope')}
                            </p>
                        </div>
                    ) : (
                        <div className="tw-space-y-3">
                            {messages.map(message => {
                                const meta = metaFor(message);
                                const isUser = message.role === 'user';
                                return (
                                    <div key={message.id}>
                                        <div
                                            className={`tw-max-w-[75%] tw-w-fit tw-rounded-md tw-px-4 tw-py-2 tw-text-sm ${isUser
                                                ? 'tw-ml-auto tw-bg-secondary tw-text-white'
                                                : 'tw-mr-auto tw-bg-white tw-text-gray-900'
                                            }`}
                                        >
                                            {isUser ? <Message message={message.content}/> : <MarkdownMessage message={message.content}/>}
                                        </div>
                                        {message.attachments?.length > 0 && (
                                            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-mt-1 tw-ml-4">
                                                {message.attachments.map(attachment => (
                                                    <div
                                                        className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-600 tw-border tw-border-solid tw-border-gray-200 tw-rounded tw-px-2 tw-py-0.5 hover:tw-border-gray-300"
                                                        key={attachment.id}
                                                    >
                                                        <a
                                                            className="tw-flex tw-items-center tw-gap-1 tw-text-gray-600 hover:tw-text-gray-900"
                                                            href={`${process.env.API_URL}/message_attachments/${attachment.id}`}
                                                        >
                                                            <FileDown className="tw-h-3 tw-w-3"/>
                                                            {attachment.filename}
                                                        </a>
                                                        <span className="tw-text-gray-400">{formatBytes(attachment.byte_size)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div
                                            className={`tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-gray-400 tw-mt-0.5 ${isUser ? 'tw-justify-end tw-mr-1' : 'tw-ml-4'}`}
                                        >
                                            <button
                                                className="hover:tw-text-gray-700"
                                                onClick={() => copyMessage(message)}
                                                type="button"
                                            >
                                                {copiedId === message.id ? <Check className="tw-h-3 tw-w-3"/> : <Copy className="tw-h-3 tw-w-3"/>}
                                            </button>
                                            {meta && <span>{meta}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                            {loading && (
                                <div>
                                    <div className="tw-max-w-[75%] tw-mr-auto tw-w-fit tw-rounded-md tw-px-4 tw-py-2 tw-text-sm tw-bg-grey-bg tw-flex tw-items-center tw-gap-2 tw-min-h-9">
                                        {[0, 1, 2].map(dot => (
                                            <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-gray-400 tw-animate-pulse" key={dot} style={{ animationDelay: `${dot * 200}ms` }}/>
                                        ))}
                                    </div>
                                    <p className="tw-text-[11px] tw-text-gray-400 tw-mt-0.5 tw-mb-0 tw-ml-0.5">{formatDuration(elapsed)}</p>
                                </div>
                            )}
                            {error && (
                                <div>
                                    <div className="tw-max-w-[75%] tw-mr-auto tw-w-fit tw-rounded-md tw-px-4 tw-py-2 tw-text-sm tw-bg-red-50 tw-text-red-700">
                                        {error.message}
                                    </div>
                                    <p className="tw-text-[11px] tw-text-gray-400 tw-mt-0.5 tw-mb-0 tw-ml-0.5 tw-flex tw-items-center tw-gap-2">
                                        <span>{[formatTime(error.at), error.elapsed > 0 && formatDuration(error.elapsed)].filter(Boolean).join(' · ')}</span>
                                        {error.retryable && (
                                            <button
                                                className="tw-flex tw-items-center tw-gap-1 tw-border tw-border-solid tw-border-gray-200 tw-rounded tw-px-2 tw-py-0.5 hover:tw-text-gray-900"
                                                onClick={retryLastQuestion}
                                                type="button"
                                            >
                                                <RefreshCw className="tw-h-3 tw-w-3"/>
                                                {i18next.t('chat.retry')}
                                            </button>
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Input — always visible */}
                <div className="tw-px-4 tw-py-3 tw-border-t tw-border-solid tw-border-gray-200 tw-bg-white tw-shrink-0">
                    <div className="tw-relative">
                        <TextArea
                            className="tw-w-full tw-h-10 !tw-min-h-0 tw-max-h-[120px] tw-resize-none tw-overflow-y-auto tw-pr-10 !tw-outline-none focus:!tw-ring-0 focus:!tw-ring-offset-0 focus-visible:!tw-ring-0 focus-visible:!tw-ring-offset-0 focus:!tw-border focus:!tw-border-slate-400 focus:!tw-py-2 focus:!tw-pl-3 focus:!tw-pr-10"
                            disabled={loading}
                            id="chat-input"
                            onChange={e => {
                                setInput(e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = `${Math.min(Math.max(e.target.scrollHeight, 40), 120)}px`;
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            placeholder={i18next.t('chat.type_message')}
                            rows={1}
                            value={input}
                        />
                        <button
                            className="tw-absolute tw-right-2 tw-bottom-2 tw-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-rounded tw-text-gray-400 hover:tw-text-gray-700 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                            disabled={loading || !input.trim() || input.trim().length > MAX_QUESTION_LENGTH}
                            onClick={sendMessage}
                            type="button"
                        >
                            <CornerDownLeft className="tw-h-4 tw-w-4"/>
                        </button>
                    </div>
                    <div className="tw-relative tw-mt-1.5">
                        <p className="tw-mb-0 tw-text-center tw-text-[11px] tw-text-gray-400">{i18next.t('chat.beta_notice')}</p>
                        <span className={`tw-absolute tw-right-0 tw-top-0 tw-text-[11px] ${input.trim().length > MAX_QUESTION_LENGTH ? 'tw-text-red-600' : 'tw-text-gray-400'}`}>
                            {input.trim().length}/{MAX_QUESTION_LENGTH}
                        </span>
                    </div>
                </div>
            </Container>
        </Background>
    );
}
